import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup } from 'reactstrap';

import apiOffers from 'api/offers';
import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import getDefaultVersion from './getDefaultVersion';
import { priceCopiedMaterial } from './subproducts/priceMaterialFromDetails';
import formatToFormStateFormat from '../formatToFormStateFormat';
import IFormState from '../IFormState';
import IVersion from '../IVersion';
import SearchPositionModal from '../../offer/SearchPositionModal';
import views from '../views';

const Header = ({
  activeVersionIndex,
  formState,
  locked,
  offerProductId,
  offerType,
  setActiveVersionIndex,
  setVersions,
  setView,
  versions,
}: {
  activeVersionIndex: number;
  formState: IFormState;
  locked: boolean;
  // The position being calculated, undefined until it is saved
  offerProductId?: number;
  // The type of the offer, e.g. preliminary-offer
  offerType?: string;
  setActiveVersionIndex: (activeVersionIndex: number) => void;
  setVersions: (versions: IVersion[]) => void;
  setView: (view: string) => void;
  versions: IVersion[];
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchIsOpen, setSearchIsOpen] = useState(false);

  const translatedUnit = t(`${translations.common}:units:${formState.unit}`);

  const getExistingVersionWithName = (name: string) =>
    versions.find((version) => version.name === name);

  const getNewVersionName = () => {
    let newVersionName = versions.length + 1;

    while (
      getExistingVersionWithName(newVersionName.toString()) !== undefined
    ) {
      newVersionName += 1;
    }

    return newVersionName.toString();
  };

  const addVersion = () => {
    setVersions([
      ...versions,
      {
        ...getDefaultVersion(),
        isPrimary: false,
        name: getNewVersionName(),
      },
    ]);

    setActiveVersionIndex(versions.length);
  };

  const copyVersion = () => {
    const selectedVersion = versions[activeVersionIndex];

    setVersions([
      ...versions,
      {
        ...getDefaultVersion(),
        isPrimary: false,
        name: getNewVersionName(),
        averageHourlyEarnings: selectedVersion.averageHourlyEarnings,
        installerCount: selectedVersion.installerCount,
        // A copy is a new base: its materials get the calculation per unit
        subproducts: selectedVersion.subproducts.map(priceCopiedMaterial),
        installations: selectedVersion.installations,
        offerText: selectedVersion.offerText,
        showInOfferPdf: selectedVersion.showInOfferPdf,
      },
    ]);

    setActiveVersionIndex(versions.length);
  };

  // The primary version of a position of an earlier offer as a new alternative: its rows
  // are read in full (the search has only the figures), the images are not copied
  const addSearchedPosition = async (position: ISearchedPosition) => {
    try {
      const source = formatToFormStateFormat(
        await apiOffers.products.get(position.offerProductId)
      ).versions.find((version) => version.isPrimary);

      if (source === undefined) {
        return;
      }

      setVersions([
        ...versions,
        {
          ...getDefaultVersion(),
          averageHourlyEarnings: source.averageHourlyEarnings,
          installations: source.installations,
          installerCount: source.installerCount,
          isPrimary: false,
          name: getNewVersionName(),
          offerText: source.offerText,
          // A copy is a new base: its materials get the calculation per unit
          subproducts: source.subproducts.map(priceCopiedMaterial),
        },
      ]);
      setActiveVersionIndex(versions.length);

      dispatch(
        addMessage(
          t(`${translations.offers}:positionSearch.Added as alternative`)
        )
      );
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:positionSearch.Copy failed`))
      );
    }
  };

  const showComingSoon = () =>
    dispatch(addMessage(t(`${translations.calculationWindow}:Coming soon`)));

  const getPrimaryText = (index: number) => {
    if (!versions[index].isPrimary) {
      return null;
    }

    return ` (${t(`${translations.calculationWindow}:primary`)})`;
  };

  const offerIsLocked = () => locked;

  // A price list row has no code and no alternatives
  const priceList = formState.isPriceListItem === true;

  const onActiveVersionIndexChange = (event: any) => {
    setActiveVersionIndex(parseInt(event.target.value, 10));
  };

  if (priceList) {
    return (
      <>
        <div className="subproducts-header">
          <h2>
            {formState.name} {`${formState.quantity}${translatedUnit}`}
          </h2>
          <div className="clearfix" />
        </div>
        <div className="calculation-window-header-actions">
          {offerIsLocked() ? null : (
            <PrimaryButton
              className="add-configuration-button"
              onClick={() => setView(views.attachConfiguration)}
            >
              {t(`${translations.calculationWindow}:Attach configuration`)}
            </PrimaryButton>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="subproducts-header">
        <h2>
          {formState.position} / {formState.name}{' '}
          {`${formState.quantity}${translatedUnit}`} /
        </h2>
        <SelectInputFieldControlled
          formGroupClassName="offer-product-version-select-form-group"
          id="offer-product-version-select"
          name="offer-product-version-select"
          onChange={onActiveVersionIndexChange}
          options={versions.map((version, index) => (
            <option key={`offer-product-version-${version.name}`} value={index}>
              {version.name}
              {getPrimaryText(index)}
            </option>
          ))}
          required
          value={activeVersionIndex}
        />
        <div className="clearfix" />
      </div>
      <div className="calculation-window-header-actions">
        {offerIsLocked() ? null : (
          <PrimaryButton
            className="add-offer-product-version"
            onClick={copyVersion}
          >
            {t(`${translations.calculationWindow}:Copy to new version`)}
          </PrimaryButton>
        )}
        {offerIsLocked() ? null : (
          <PrimaryButton
            className="add-offer-product-version"
            onClick={addVersion}
          >
            {t(`${translations.calculationWindow}:Add version`)}
          </PrimaryButton>
        )}
        {offerIsLocked() ? null : (
          <PrimaryButton
            className="add-offer-product-version"
            onClick={() => setSearchIsOpen(true)}
          >
            {t(`${translations.offers}:positionSearch.Open for alternative`)}
          </PrimaryButton>
        )}
        {offerIsLocked() ? null : (
          // Material prices from a supplier's offer or invoice: not in use yet
          <div
            className="material-price-buttons"
            role="group"
            aria-label={t(
              `${translations.calculationWindow}:Get material price`
            )}
          >
            <span className="material-price-label">
              {t(`${translations.calculationWindow}:Get material price`)}
            </span>
            <ButtonGroup size="sm">
              <Button
                className="material-price-button"
                onClick={showComingSoon}
              >
                {t(`${translations.calculationWindow}:From offer`)}
              </Button>
              <Button
                className="material-price-button"
                onClick={showComingSoon}
              >
                {t(`${translations.calculationWindow}:From invoice`)}
              </Button>
            </ButtonGroup>
          </div>
        )}
        {offerIsLocked() ? null : (
          <PrimaryButton
            className="add-configuration-button"
            onClick={() => setView(views.attachConfiguration)}
          >
            {t(`${translations.calculationWindow}:Attach configuration`)}
          </PrimaryButton>
        )}
      </div>
      {searchIsOpen ? (
        <SearchPositionModal
          isOpen
          offerProductId={offerProductId}
          onSelect={addSearchedPosition}
          toggle={() => setSearchIsOpen(false)}
          type={offerType ?? ''}
        />
      ) : null}
    </>
  );
};

export default Header;
